import { useState, useEffect, useCallback } from 'react';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { SyncService } from '../services/sync/SyncService';
import { agentEntriesApi } from '../services/api/endpoints/agentEntries';
import { journalEntriesApi } from '../services/api/endpoints/journalEntries';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import { useJournalActivity } from './useActivityTracking';
import type { JournalEntry, JournalLine } from '../types/accounting';

// Callback pour ajouter une entrée validée au journal principal
type OnEntryValidatedCallback = (entry: JournalEntry) => void;

/**
 * Interface pour les entrées de l'agent provenant de l'API
 * Structure: { id, agentId, proposedEntry: { date, lines, ... }, confidence, validationStatus, ... }
 */
interface AgentEntryFromAPI {
  id: string;
  agentId: string;
  agentVersion?: string;
  proposedEntry: {
    date: string;
    journalType: 'sales' | 'purchases' | 'bank' | 'cash' | 'general' | 'inventory' | 'financial' | 'miscellaneous';
    description: string;
    reference: string;
    lines: Array<{
      accountCode: string;
      accountName: string;
      debit: number;
      credit: number;
      description: string;
      vatCode?: string;
      vatAmount?: number;
      confidence?: number;
    }>;
    totalDebit: number;
    totalCredit: number;
    totalVat: number;
  };
  sourceDocument?: {
    id: string;
    name: string;
    type: string;
    url: string;
    analyzedAt: string;
  };
  confidence: number | string;
  validationStatus: 'pending' | 'validated' | 'rejected';
  validatedBy?: string;
  validatedAt?: string;
  rejectionReason?: string;
  journalEntryId?: string;
  businessUnitId?: string;
  businessUnitType?: 'company' | 'branch' | 'pos';
  fiscalYearId?: string;
  companyId?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Mapper une entrée API AgentEntry vers JournalEntry pour l'UI
 * L'API retourne proposedEntry imbriqué, l'UI attend une structure plate
 */
function mapAgentEntryToJournalEntry(apiEntry: AgentEntryFromAPI): JournalEntry {
  const { proposedEntry } = apiEntry;
  
  // Mapper les lignes avec des IDs générés si non présents
  const lines: JournalLine[] = (proposedEntry?.lines || []).map((line, index) => ({
    id: `${apiEntry.id}-line-${index}`,
    accountId: line.accountCode, // Utiliser accountCode comme fallback pour accountId
    accountCode: line.accountCode || '',
    accountName: line.accountName || '',
    debit: line.debit || 0,
    credit: line.credit || 0,
    description: line.description || '',
    vatCode: line.vatCode,
    vatAmount: line.vatAmount
  }));

  return {
    id: apiEntry.id,
    fiscalYearId: apiEntry.fiscalYearId,
    businessUnitId: apiEntry.businessUnitId,
    businessUnitType: apiEntry.businessUnitType,
    date: proposedEntry?.date || new Date().toISOString().split('T')[0],
    journalType: proposedEntry?.journalType || 'general',
    description: proposedEntry?.description || 'Opération générée par ADHA',
    reference: proposedEntry?.reference || `ADHA-${apiEntry.id.substring(0, 8).toUpperCase()}`,
    totalDebit: proposedEntry?.totalDebit || 0,
    totalCredit: proposedEntry?.totalCredit || 0,
    totalVat: proposedEntry?.totalVat || 0,
    status: 'draft', // Les entrées agent commencent en brouillon
    source: 'agent',
    agentId: apiEntry.agentId,
    confidence: typeof apiEntry.confidence === 'string' ? parseFloat(apiEntry.confidence) : apiEntry.confidence,
    sourceDocument: apiEntry.sourceDocument ? {
      id: apiEntry.sourceDocument.id,
      name: apiEntry.sourceDocument.name,
      type: apiEntry.sourceDocument.type,
      url: apiEntry.sourceDocument.url,
    } : undefined,
    validationStatus: apiEntry.validationStatus || 'pending',
    validatedBy: apiEntry.validatedBy,
    validatedAt: apiEntry.validatedAt,
    rejectionReason: apiEntry.rejectionReason,
    lines,
    createdBy: apiEntry.createdBy,
    createdAt: apiEntry.createdAt,
    updatedAt: apiEntry.updatedAt
  };
}

/**
 * Vérifier si un objet est déjà au format JournalEntry (avec lines à la racine)
 * ou s'il est au format API (avec proposedEntry)
 */
function isAlreadyJournalEntry(entry: any): entry is JournalEntry {
  return Array.isArray(entry.lines) && !entry.proposedEntry;
}

/**
 * Mapper intelligemment - si déjà JournalEntry, retourner tel quel
 * Sinon, convertir de AgentEntryFromAPI vers JournalEntry
 */
function smartMapEntry(entry: any): JournalEntry {
  if (isAlreadyJournalEntry(entry)) {
    // S'assurer que source est défini pour les entrées agent
    return {
      ...entry,
      source: entry.source || 'agent',
      validationStatus: entry.validationStatus || 'pending'
    };
  }
  return mapAgentEntryToJournalEntry(entry as AgentEntryFromAPI);
}

let onEntryValidatedCallback: OnEntryValidatedCallback | null = null;

export function setOnEntryValidatedCallback(callback: OnEntryValidatedCallback) {
  onEntryValidatedCallback = callback;
}

// Clé localStorage pour persister les IDs validés
const VALIDATED_IDS_STORAGE_KEY = 'wanzo_validated_agent_entry_ids';

// Set pour suivre les IDs des entrées validées localement (pour éviter leur réapparition)
const validatedEntryIds = new Set<string>();

// Charger les IDs validés depuis localStorage au démarrage
function loadValidatedIdsFromStorage(): void {
  try {
    const stored = localStorage.getItem(VALIDATED_IDS_STORAGE_KEY);
    if (stored) {
      const ids = JSON.parse(stored) as string[];
      ids.forEach(id => validatedEntryIds.add(id));
      console.log(`📥 Loaded ${ids.length} validated entry IDs from storage`);
    }
  } catch (e) {
    console.warn('Failed to load validated IDs from storage:', e);
  }
}

// Sauvegarder les IDs validés dans localStorage
function saveValidatedIdsToStorage(): void {
  try {
    const ids = Array.from(validatedEntryIds);
    localStorage.setItem(VALIDATED_IDS_STORAGE_KEY, JSON.stringify(ids));
    console.log(`💾 Saved ${ids.length} validated entry IDs to storage`);
  } catch (e) {
    console.warn('Failed to save validated IDs to storage:', e);
  }
}

// Ajouter un ID validé et persister
function markEntryAsValidated(id: string): void {
  validatedEntryIds.add(id);
  saveValidatedIdsToStorage();
  console.log(`✅ Marked entry ${id} as validated (total: ${validatedEntryIds.size})`);
}

// Retirer un ID (en cas de rollback) et persister
function unmarkEntryAsValidated(id: string): void {
  validatedEntryIds.delete(id);
  saveValidatedIdsToStorage();
  console.log(`↩️ Unmarked entry ${id} from validated (total: ${validatedEntryIds.size})`);
}

/**
 * Nettoyer la liste des IDs validés
 * Utile pour réinitialiser l'état après une synchronisation complète avec le backend
 * ou pour le débogage
 */
export function clearValidatedEntryIds(): void {
  validatedEntryIds.clear();
  localStorage.removeItem(VALIDATED_IDS_STORAGE_KEY);
  console.log('🗑️ Cleared all validated entry IDs');
}

/**
 * Obtenir le nombre d'IDs validés en mémoire
 */
export function getValidatedEntryIdsCount(): number {
  return validatedEntryIds.size;
}

// Initialiser au chargement du module
loadValidatedIdsFromStorage();

export function useAgentEntries() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();
  const { logJournalValidation, logJournalStateChange } = useJournalActivity();

  /**
   * Filtrer les entrées pour exclure celles qui sont déjà validées ou en cours de validation
   * Cette fonction est cruciale pour éviter que les entrées validées réapparaissent
   */
  const filterPendingEntries = useCallback((entriesToFilter: JournalEntry[]): JournalEntry[] => {
    return entriesToFilter.filter(entry => {
      // Exclure les entrées déjà marquées comme validées localement
      if (validatedEntryIds.has(entry.id)) {
        console.log(`🚫 Filtering out validated entry: ${entry.id}`);
        return false;
      }
      // Exclure les entrées avec status 'validated'
      if (entry.validationStatus === 'validated') {
        console.log(`🚫 Filtering out entry with validated status: ${entry.id}`);
        return false;
      }
      // Garder seulement les entrées en attente
      return true;
    });
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fiscalYearId = currentFiscalYear?.id;
      
      // Charger depuis IndexedDB d'abord pour une expérience rapide
      const localEntries = await IndexedDBService.getAll<any>('agent_entries');
      // Mapper les entrées locales vers JournalEntry
      let mappedLocalEntries = localEntries.map(smartMapEntry);
      
      // IMPORTANT: Filtrer les entrées déjà validées
      mappedLocalEntries = filterPendingEntries(mappedLocalEntries);
      
      let filteredLocal = fiscalYearId 
        ? mappedLocalEntries.filter(e => e.fiscalYearId === fiscalYearId || !e.fiscalYearId)
        : mappedLocalEntries;
      
      // Filtrer par unité commerciale si définie
      if (effectiveFilter.businessUnitId) {
        filteredLocal = filteredLocal.filter(e => 
          (e as any).businessUnitId === effectiveFilter.businessUnitId ||
          effectiveFilter.businessUnitIds?.includes((e as any).businessUnitId) ||
          !(e as any).businessUnitId
        );
      }
      
      if (filteredLocal.length > 0) {
        setEntries(filteredLocal);
      }

      // Si en ligne, synchroniser avec l'API
      if (navigator.onLine) {
        try {
          const response = await agentEntriesApi.getAll({ 
            fiscalYearId,
            businessUnitId: effectiveFilter.businessUnitId,
            businessUnitType: effectiveFilter.businessUnitType
          });
          
          if (response.success && response.data) {
            const rawApiEntries = Array.isArray(response.data.data) ? response.data.data : [];
            // Mapper les entrées API vers JournalEntry
            let mappedApiEntries = rawApiEntries.map(smartMapEntry);
            
            // IMPORTANT: Filtrer les entrées déjà validées (même celles de l'API)
            mappedApiEntries = filterPendingEntries(mappedApiEntries);
            
            console.log('📥 Agent entries from API:', rawApiEntries.length, '-> After filtering:', mappedApiEntries.length);
            setEntries(mappedApiEntries);
            
            // Mettre à jour IndexedDB avec seulement les entrées NON validées
            await IndexedDBService.clear('agent_entries');
            for (const entry of mappedApiEntries) {
              await IndexedDBService.add('agent_entries', entry);
            }
            return;
          }
        } catch (apiError) {
          console.warn('⚠️ API agent entries failed, using fallback:', apiError);
        }
      }
      
      // PRODUCTION: Pas de fallback vers les données mock - utiliser tableau vide
      // Les entrées agent doivent venir de l'API ou IndexedDB uniquement
      if (entries.length === 0) {
        console.log('📭 No agent entries available');
        setEntries([]);
      }
      
    } catch (error) {
      console.error('Failed to load agent entries:', error);
      setError('Erreur lors du chargement des écritures proposées par l\'agent');
      
      // PRODUCTION: Fallback vers tableau vide au lieu des mocks
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [currentFiscalYear?.id, effectiveFilter.businessUnitId, effectiveFilter.businessUnitType, effectiveFilter.businessUnitIds]);

  // Recharger quand l'exercice comptable ou l'unité commerciale change
  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  /**
   * Fonction utilitaire pour rollback en cas d'échec
   */
  const rollbackValidation = async (originalEntry: JournalEntry, validatedEntryId: string) => {
    // Retirer l'ID du Set des entrées validées pour permettre sa réapparition
    unmarkEntryAsValidated(originalEntry.id);
    
    // Restaurer l'entrée dans agent_entries
    await IndexedDBService.add('agent_entries', originalEntry);
    setEntries(prev => [originalEntry, ...prev]);
    
    // Supprimer de journal_entries si elle a été ajoutée
    try {
      await IndexedDBService.delete('journal_entries', validatedEntryId);
    } catch (e) {
      // Ignore
    }
  };
  
  // Valider une entrée individuelle - TRANSFÈRE vers le journal principal
  const validateEntry = async (id: string, updates?: Partial<JournalEntry>): Promise<JournalEntry> => {
    setLoading(true);
    
    // Protection contre les données undefined
    const safeEntries = Array.isArray(entries) ? entries : [];
    
    const entryToValidate = safeEntries.find(e => e.id === id);
    
    if (!entryToValidate) {
      setLoading(false);
      throw new Error('Entry not found');
    }
    
    // IMPORTANT: Marquer immédiatement l'entrée comme validée pour éviter sa réapparition
    markEntryAsValidated(id);
    
    // Sauvegarder l'état original pour rollback
    const originalEntry = { ...entryToValidate };
    
    // Créer l'entrée validée avec les mises à jour
    const validatedEntry: JournalEntry = {
      ...entryToValidate,
      ...updates,
      source: 'agent',
      status: 'posted', // L'écriture validée est directement comptabilisée
      validationStatus: 'validated',
      validatedAt: new Date().toISOString(),
      fiscalYearId: currentFiscalYear?.id || entryToValidate.fiscalYearId
    };
    
    try {
      // 1. Supprimer l'entrée de la liste des entrées d'agent (optimiste)
      setEntries(prev => prev.filter(e => e.id !== id));
      
      // 2. Ajouter aux entrées de journal régulières via IndexedDB
      await IndexedDBService.add('journal_entries', validatedEntry);
      
      // 3. Supprimer de IndexedDB agent_entries si elle existe
      try {
        await IndexedDBService.delete('agent_entries', id);
      } catch (e) {
        // Ignore si l'entrée n'existe pas dans IndexedDB
      }
      
      // 4. Si en ligne, valider avec l'API agent-entries
      if (navigator.onLine) {
        try {
          // Appeler l'API de validation des agent entries
          // POST /agent-entries/:id/validate
          const response = await agentEntriesApi.validate(id, updates);
          console.log('✅ Agent entry validated via API:', response);
          
          // Si l'API a retourné un journalEntryId, mettre à jour l'entrée locale
          if (response.success && response.data?.journalEntryId) {
            validatedEntry.id = response.data.journalEntryId;
          }
        } catch (apiError) {
          console.error('API validation failed, rolling back:', apiError);
          // Rollback en cas d'échec API
          await rollbackValidation(originalEntry, validatedEntry.id);
          throw new Error('Échec de la validation. L\'écriture a été restaurée.');
        }
      } else {
        // Hors ligne: ajouter à la file de synchronisation pour validation ultérieure
        await SyncService.addToSyncQueue({
          id: validatedEntry.id,
          type: 'CREATE',
          entity: 'agent_entry_validation',
          data: { agentEntryId: id, updates, validatedEntry }
        });
      }
      
      // 5. Notifier via le callback si disponible
      if (onEntryValidatedCallback) {
        onEntryValidatedCallback(validatedEntry);
      }
      
      // 6. Logger l'activité
      logJournalValidation(validatedEntry.description || validatedEntry.reference || 'Écriture ADHA', 1);
      
      console.log(`Entrée ${id} validée et transférée au journal principal:`, validatedEntry);
      return validatedEntry;
    } catch (error) {
      console.error('Failed to validate agent entry:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Valider plusieurs entrées à la fois - TRANSFÈRE vers le journal principal
  const validateBatchEntries = async (ids: string[]): Promise<number> => {
    setLoading(true);
    
    const safeEntries = Array.isArray(entries) ? entries : [];
    const entriesToValidate = safeEntries.filter(e => ids.includes(e.id));
    
    // IMPORTANT: Marquer immédiatement TOUTES les entrées comme validées pour éviter leur réapparition
    ids.forEach(id => {
      markEntryAsValidated(id);
    });
    
    // Sauvegarder les états originaux pour rollback potentiel
    const originalEntries = entriesToValidate.map(e => ({ ...e }));
    const validatedEntries: JournalEntry[] = [];
    let validatedCount = 0;
    
    try {
      for (const entry of entriesToValidate) {
        // Créer l'entrée validée
        const validatedEntry: JournalEntry = {
          ...entry,
          source: 'agent',
          status: 'posted',
          validationStatus: 'validated',
          validatedAt: new Date().toISOString(),
          fiscalYearId: currentFiscalYear?.id || entry.fiscalYearId
        };
        
        // Ajouter aux entrées de journal régulières via IndexedDB
        await IndexedDBService.add('journal_entries', validatedEntry);
        
        // Supprimer de IndexedDB agent_entries
        try {
          await IndexedDBService.delete('agent_entries', entry.id);
        } catch (e) {
          // Ignore
        }
        
        validatedEntries.push(validatedEntry);
        validatedCount++;
      }
      
      // Supprimer les entrées validées de la liste des entrées d'agent
      setEntries(prev => prev.filter(e => !ids.includes(e.id)));
      
      // Synchroniser avec l'API si en ligne
      if (navigator.onLine) {
        try {
          // Utiliser l'endpoint batch: POST /agent-entries/batch/validate
          const response = await agentEntriesApi.validateBatch(ids);
          
          if (response.success && response.data) {
            const { validated, skipped, failed, results } = response.data;
            
            // Si certaines validations ont échoué, log les erreurs
            if (failed > 0 || skipped > 0) {
              const failedResults = results?.filter(r => r.status !== 'validated') || [];
              failedResults.forEach(r => {
                console.warn(`Entry ${r.id} ${r.status}: ${r.error || 'Unknown reason'}`);
              });
            }
          }
        } catch (batchError: any) {
          // Si le batch échoue, fallback sur validations individuelles
          console.warn('Batch validation failed, using individual validations:', batchError?.message);
          
          let failureCount = 0;
          for (const id of ids) {
            try {
              await agentEntriesApi.validate(id);
            } catch (individualError) {
              console.error(`Failed to validate entry ${id}:`, individualError);
              failureCount++;
            }
          }
          
          // Si toutes les validations échouent, rollback
          if (failureCount === ids.length) {
            for (let i = 0; i < originalEntries.length; i++) {
              await rollbackValidation(originalEntries[i], validatedEntries[i]?.id || originalEntries[i].id);
            }
            throw new Error('Échec de la validation. Les écritures ont été restaurées.');
          }
        }
      } else {
        // Hors ligne: ajouter à la file de synchronisation pour validation ultérieure
        for (const id of ids) {
          await SyncService.addToSyncQueue({
            id: `validate-${id}-${Date.now()}`,
            type: 'CREATE',
            entity: 'agent_entry_validation',
            data: { agentEntryId: id }
          });
        }
      }
      
      // Notifier via le callback
      for (const validatedEntry of validatedEntries) {
        if (onEntryValidatedCallback) {
          onEntryValidatedCallback(validatedEntry);
        }
      }
      
      // Logger l'activité de validation en lot
      if (validatedCount > 0) {
        logJournalValidation('Validation en lot par l\'agent comptable', validatedCount);
      }
      
      console.log(`${validatedCount} entrées validées et transférées au journal principal`);
      return validatedCount;
    } catch (error) {
      console.error('Failed to validate batch entries:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Rejeter une entrée (supprime sans transférer)
  const rejectEntry = async (id: string, reason: string): Promise<void> => {
    try {
      setLoading(true);
      
      // IMPORTANT: Marquer l'entrée comme traitée pour éviter sa réapparition
      markEntryAsValidated(id); // On utilise le même mécanisme pour les rejets
      
      // Récupérer l'entrée avant suppression pour le log
      const safeEntries = Array.isArray(entries) ? entries : [];
      const entryToReject = safeEntries.find(e => e.id === id);
      
      // Supprimer de la liste des entrées d'agent (optimiste)
      setEntries(prev => prev.filter(e => e.id !== id));
      
      // Supprimer de IndexedDB
      try {
        await IndexedDBService.delete('agent_entries', id);
      } catch (e) {
        // Ignore
      }
      
      // Appeler l'API si en ligne: POST /agent-entries/:id/reject
      if (navigator.onLine) {
        try {
          await agentEntriesApi.reject(id, reason);
          console.log('✅ Agent entry rejected via API');
        } catch (apiError) {
          console.warn('API reject failed, but local state updated:', apiError);
        }
      }
      
      // Logger l'activité
      if (entryToReject) {
        logJournalStateChange(
          'Écriture ADHA rejetée', 
          `${entryToReject.description || entryToReject.reference || 'Écriture'} - ${reason}`,
          'warning'
        );
      }
      
      console.log(`Entrée ${id} rejetée. Raison: ${reason}`);
    } catch (error) {
      console.error('Failed to reject agent entry:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour une entrée avant validation
  const updateEntry = async (id: string, updates: Partial<JournalEntry>): Promise<void> => {
    try {
      setLoading(true);
      
      // Protection contre les données undefined
      const safeEntries = Array.isArray(entries) ? entries : [];
      
      // En mode développement, simuler la mise à jour
      if (true) { // Remplacer par une condition comme process.env.NODE_ENV === 'development'
        const entryIndex = safeEntries.findIndex(e => e.id === id);
        if (entryIndex === -1) throw new Error('Entry not found');
        
        const updatedEntry = { ...safeEntries[entryIndex], ...updates };
        const newEntries = [...safeEntries];
        newEntries[entryIndex] = updatedEntry as JournalEntry;
        
        setEntries(newEntries);
        console.log(`Entrée ${id} mise à jour avec succès:`, updatedEntry);
        return;
      }
      
      // En mode production, utiliser ce code:
      /*
      const response = await agentEntriesApi.update(id, updates);
      
      if (response.success && response.data) {
        // Mettre à jour localement
        await IndexedDBService.update('agent_entries', response.data);
        setEntries(prev => {
          const newEntries = [...prev];
          const index = newEntries.findIndex(e => e.id === id);
          if (index !== -1) {
            newEntries[index] = response.data;
          }
          return newEntries;
        });
      }
      */
    } catch (error) {
      console.error('Failed to update agent entry:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une entrée agent
  const deleteEntry = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      
      // Appeler l'API pour supprimer l'entrée agent
      await agentEntriesApi.delete(id);
      console.log(`✅ Entrée agent ${id} supprimée de l'API`);
      
      // Supprimer de IndexedDB
      try {
        await IndexedDBService.delete('agent_entries', id);
        console.log(`✅ Entrée agent ${id} supprimée de IndexedDB`);
      } catch (dbError) {
        console.warn('⚠️ Could not delete from IndexedDB:', dbError);
        // Continuer même si IndexedDB échoue - l'API a réussi
      }
      
      // Mettre à jour l'état local
      setEntries(prev => prev.filter(e => e.id !== id));
      console.log(`✅ Entrée agent ${id} supprimée de l'état local`);
    } catch (error) {
      console.error('❌ Failed to delete agent entry:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    entries,
    loading,
    error,
    loadEntries,
    validateEntry,
    validateBatchEntries,
    rejectEntry,
    updateEntry,
    deleteEntry
  };
}
